import { AxiosError } from 'axios';
import { useQuery } from 'react-query';
import { Axios } from '@utils/api/axios-instance.ts';
import { GET_URL_NEW } from '@utils/api/endpoints.ts';
import { BaseError, BaseResponse } from '@utils/types/shared/base.ts';

/**
 * One row of GET /speech-to-text/session. The list endpoint returns the
 * session's zoom id as `id` (not `session_id`).
 */
export type ReplacementSessionOption = {
  id: string;
  filename: string | null;
  status: string;
  duration: number | null;
  date_session_start: string;
  is_deleted: boolean;
};

const PAGE_SIZE = 20;

/**
 * The search API splits `q` on commas and treats `%` as a wildcard, so both
 * are removed from what the editor typed.
 */
export const toFilenameQuery = (text: string): string | undefined => {
  const cleaned = text.replace(/[,%]/g, ' ').trim();
  return cleaned ? `filename:ilike:${cleaned}` : undefined;
};

/**
 * Sessions in the caller's organization that could replace `excludeId`'s
 * media. Reuses the workspace session list so access rules stay in one
 * place: the API only ever returns the caller's own organization.
 */
export function useSearchReplacementSessions(
  search: string,
  excludeId: string,
  enabled: boolean
) {
  const q = toFilenameQuery(search);
  return useQuery<ReplacementSessionOption[], AxiosError<BaseError>>(
    ['replacement-sessions', q ?? ''],
    async () => {
      const { sttSessions } = GET_URL_NEW({
        offset: 0,
        limit: PAGE_SIZE,
        q,
        sort: 'date_session_start',
        direction: 'desc'
      });
      const { data } =
        await Axios.get<BaseResponse<ReplacementSessionOption[]>>(sttSessions);
      return data.result ?? [];
    },
    {
      enabled,
      staleTime: 30 * 1000,
      keepPreviousData: true,
      select: rows =>
        rows.filter(row => row.id !== excludeId && !row.is_deleted)
    }
  );
}
